import { Play, Users, Heart } from 'lucide-react';

// Placeholder founder data - update with actual content
const founders = [
  {
    id: 1,
    name: 'Akil Piggott, PT, DPT, OCS, ACSM EP-C',
    image: '/images/founders/akilpiggott.webp', // Update path as needed
    bio: 'Akil Piggott is a practicing Doctor of Physical Therapy, residency trained Board Certified Orthopedic Specialist, and residency mentor/adjunct educator at Thomas Jefferson University. With added insight from over 6 years of personal training, Akil bridges the gap between rehabilitation and performance. Akil is responsible for revamping ACL rehab at The Training Room of Cherry Hill, a South Jersey hub for athletes recovering from ACL injuries. He is the co-founder and CEO of 3M Athletic Performance.',
  },
  {
    id: 2,
    name: 'Travis Pollen, PhD',
    image: '/images/founders/travispollen.webp', // Update path as needed
    bio: 'Travis Pollen is a personal trainer, two-time author, PhD in Health and Rehabilitation Sciences, and adjunct professor at Drexel University. His research focuses on risk factors for injury in competitive athletes and return to sport testing post-ACL injury. He also holds a master’s degree in Biomechanics and Movement Science from University of Delaware, a nationally-recognized institution for ACL research. He is the co-founder and COO of 3M Athletic Performance.',
  },
  // Add more founders as needed
];

export function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">About ACL Care</h1>
        <p className="text-gray-600">Learn about our program and mission</p>
      </div>

      {/* Welcome Video Section */}
      <section className="mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <Play className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Welcome to ACL Care Pro</h2>
            </div>
          </div>
          <div className="aspect-video bg-gray-100">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/ebnVwiJq6_I"
              title="Welcome to ACL Care Pro"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Why Is This So Important Section */}
      <section className="mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <Heart className="h-5 w-5 text-red-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Why Is This So Important?</h2>
            </div>
          </div>
          <div className="aspect-video bg-gray-100">
            {/* Replace with actual video embed URL */}
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/kzdWlk8wi9A"
              title="Why Is This So Important?"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* About Blurb */}
      <section className="mb-8">
        <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What is ACL CARE?</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed">
              ACL CARE is a comprehensive 14+ week training program designed to maximize recovery 
              and standardize care for patients once they enter "rehab purgatory." Rehab purgatory 
              is the time period after a patient is discharged from formal physical therapy but is 
              still months away from clearance to return to sport or other high level physical activity.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              ACL CARE exposes patients to strength and movement milestones necessary for returning 
              to athletic activities with a minimized risk of re-injury. The program is adaptable 
              for patients of all levels and recovery rates and can be completed with minimal equipment.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Data captured throughout the program can be used to inform coaches, physical therapists, 
              and physicians of progress to aid in clearance decision-making.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <Users className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Meet the Founders</h2>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid gap-6 md:grid-cols-2">
              {founders.map((founder) => (
                <div key={founder.id} className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-xl">
                  {/* Founder Image */}
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 mb-4">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder if image doesn't load
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-primary/10">
                            <span class="text-3xl font-bold text-primary">
                              ${founder.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                        `;
                      }}
                    />
                  </div>
                  
                  {/* Founder Name */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {founder.name}
                  </h3>
                  
                  {/* Founder Bio */}
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {founder.bio}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact/More Info */}
      <section className="mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-2">Want to learn more?</h3>
          <p className="text-gray-600 mb-4">
            Visit our website for additional information about ACL Care Pro.
          </p>
          <a
            href="https://www.aclcarepro.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
          >
            Visit aclcarepro.com
          </a>
        </div>
      </section>
    </div>
  );
}